import { get, set, reduce, merge, assign, sortBy, cloneDeep, result, size } from 'lodash';
import { initialize, reset } from 'redux-form';

import { listRequest, listRequestError, listRequestSuccess, setForceUpdate, setTotalCount } from '../../common/components/SSPList/actions';
import { DIAGNOSTICS_CHARGER_TASK_TYPE } from '../../common/constants/chargerTask';
import ChargersTask from '../../common/resources/ChargersTask';
import APIClient from '../../common/services/APIClient';
import ChargerPreprocessor from '../../common/services/ChargerPreprocessor';
import { exportFile, getExportPayload } from '../../common/services/exportService';
import { handleChargerError } from './chargerErrorHandler';
import { activitiesTabTableId } from './components/ActivitiesTab';
import { processPackagesFieldName } from './components/ChargerConfiguration/ChargerConfiguration';
import { chargerConfigurationFormId, parameterStatuses } from './components/ChargerConfiguration/constants';
import { sessionsTabTableId } from './components/ChargingSessionsTab';
import { faultsSSPId } from './components/FaultsTab';
import {
  getChargerConfigurationFormData,
  getViewedChargerConfiguration,
  getViewedChargerConfigurationParametersByStatus,
  getViewedChargerFieldsPathMap,
  getViewedChargerUuid,
} from './selectors';
import { CHARGER_STATUS_AVAILABLE } from '../../common/constants/statuses';

let requestController = null;

export const toggleReinitForm = (flag) => ({
  type: 'TOGGLE_REINIT_FORM',
  flag,
});

export const setActiveCharger = (currentCharger) => ({
  type: 'SET_ACTIVE_CHARGER_FORM',
  currentCharger,
});

export const initViewEditChargerForm = (data) => (dispatch) => {
  const processedData = ChargerPreprocessor.process(data);

  dispatch(initialize('chargerDetails', { ...processedData, _google_api_country_names: [] }));
  dispatch(setActiveCharger(processedData));
};

export const chargerRequest = () => ({
  type: 'CHARGER_REQUEST',
});

export const getChargerDetails = (uuid) => (dispatch) => {
  dispatch(chargerRequest());
  APIClient.get(`/chargers/${uuid}/details`).then(({ data }) => {
    if (data) {
      dispatch(initViewEditChargerForm(data));
      dispatch(toggleReinitForm(true));
    }
  });
};

export const toggleChargerDetailsFetching = (flag) => ({
  type: 'TOGGLE_FETCHING',
  flag,
});

export const toggleSnackbar = (flag) => ({
  type: 'TOGGLE_SNACKBAR',
  flag,
});

const resetRequestSuccess = (command) => ({
  type: 'RESET_REQUEST_SUCCESS',
  command,
});

const resetRequestError = (code, message) => ({
  type: 'RESET_REQUEST_ERROR',
  code,
  message,
});

export const setResetCommand = (command) => ({
  type: 'SET_RESET_COMMAND',
  command,
});

export const openResetDialog = (command) => ({
  type: 'OPEN_CHARGER_RESET_DIALOG',
  command,
});

export const closeResetDialog = () => ({
  type: 'CLOSE_CHARGER_RESET_DIALOG',
});

export const setChargerConfiguration = (configuration) => ({
  type: 'SET_CHARGER_CONFIGURATION',
  configuration,
});

export const setConfigurationFieldPathMap = (fieldsPathMap) => ({
  type: 'SET_CONFIGURATION_FIELD_MAP',
  fieldsPathMap,
});

export const setNonLiveParametersExist = (isExist) => ({
  type: 'SET_NON_LIVE_PARAMETERS_EXIST',
  isExist,
});

export const selectChargerConfigurationModule = (moduleName) => ({
  type: 'SELECT_CHARGER_CONFIGURATION_MODULE',
  moduleName,
});

export const toggleConfigurationConfirmationDialogOpen = (isOpen) => ({
  type: 'TOGGLE_CONFIGURATION_CONFIRMATION_DIALOG_OPEN',
  isOpen,
});

export const setConfigurationParametersSubmitError = (message) => ({
  type: 'SET_CONFIGURATION_PARAMETERS_SUBMIT_ERROR',
  message,
});

export const setConfigurationSubmitFetching = (isFetching) => ({
  type: 'SET_CONFIGURATION_SUBMIT_FETCHING',
  isFetching,
});

export const setChargerConfigurationLockInterval = (lockInterval) => ({
  type: 'SET_CHARGER_CONFIGURATION_LOCK_INTERVAL',
  lockInterval,
});

export const setChargerConfigurationLockedIds = (ids) => ({
  type: 'SET_CHARGER_CONFIGURATION_LOCKED_IDS',
  ids,
});

export const toggleShowConfigurationLockedPopup = (showLockedPopup) => ({
  type: 'TOGGLE_SHOW_CONFIGURATION_LOCKED_POPUP',
  showLockedPopup,
});

export const setDiagnosticsAvailable = (value) => ({
  type: 'SET_DIAGNOSTICS_AVAILABLE',
  payload: value,
});

export const fromMonitoredToAvailable = (charger) => (dispatch) => {
  if (charger) {
    const cUuid = get(charger, 'c_uuid');

    if (cUuid) {
      APIClient.put(`/chargers/${cUuid}/control/available`).then(() => {
        set(charger, 'i2p2_state.c_status', CHARGER_STATUS_AVAILABLE.toUpperCase());
        dispatch(initialize('chargerDetails', charger, true));
        dispatch(setActiveCharger(charger));
        dispatch(toggleSnackbar(true));
      });
    }
  }
};

export const updateChargerBody = (chargerBody) => (dispatch) => {
  dispatch(chargerRequest());
  const filtered = ChargerPreprocessor.getChargerSavingModel(chargerBody);

  dispatch(toggleChargerDetailsFetching(true));

  return APIClient.put(`/chargers/${chargerBody.c_uuid}`, filtered)
    .then(({ data }) => {
      dispatch(initViewEditChargerForm(data));
      dispatch(toggleSnackbar(true));
      dispatch(setForceUpdate('activitiesList'));
      dispatch(toggleReinitForm(true));
      dispatch(toggleChargerDetailsFetching(false));
    })
    .catch((e) => {
      dispatch(toggleChargerDetailsFetching(false));
      dispatch(handleChargerError(e));
    });
};

export const resetCharger = (id, command) => (dispatch) => {
  dispatch(chargerRequest());
  APIClient.put(`/chargers/${id}/control/reset`, { resetCommand: command })
    .then(() => {
      dispatch(resetRequestSuccess(command));
      dispatch(setForceUpdate('activitiesList'));
    })
    .catch(
      ({
        response: {
          data: { code, message },
        },
      }) => {
        dispatch(setForceUpdate('activitiesList'));
        if (code && message) {
          if (message.includes('No response received') || message.includes('Timeout error.')) {
            message = "Device didn't respond. Please try again.";
          }

          if (message.includes('Internal error')) {
            message = 'Internal error occurred. Please see Charger Activities for more details.';
          }

          dispatch(resetRequestError(code, message));
        }
      },
    );
};

export const getSSPData = (id, dynamicSectionName, chargerId, rest) => (dispatch) => {
  const {
    page_token: pageToken = 0,
    page_size: pageSize = 10,
    filter_by: filterBy = [],
    sorting = {},
    url = `/chargers/${chargerId}/${id}`,
    method = 'post',
    polling = false,
  } = rest;

  if (requestController !== null && requestController.abort) {
    requestController.abort();
    requestController = null;
  }

  if (!polling) dispatch(listRequest(id));

  try {
    const filter = reduce(
      filterBy,
      (newResult, item) => {
        if (item.isDynamicField) {
          return merge({}, newResult, { [dynamicSectionName]: { [item.id]: item.value } });
        }

        return assign({}, newResult, { [item.id]: item.value });
      },
      {},
    );

    requestController = new AbortController();

    APIClient[method](
      url,
      { filter_by: filter }, // body
      {
        signal: requestController.signal,
        params: {
          page_token: pageToken,
          page_size: pageSize,
          sort_by: sorting.id,
          sort_order: sorting.desc ? 'DESC' : 'ASC',
        }, // query string
      },
    )
      .then((resp) => {
        requestController = null;
        const data = result(resp, 'data.payloads', null);
        const size = result(resp, 'data.total_size', 0);
        const filteredData = data.filter((item) => {
          if (id === activitiesTabTableId && !!item) {
            return item.field_path !== 'i2p2_state/is_auto_activated' && (item.field_path || '').indexOf('i2p2_state/system_generated_values') === -1
          }
          return !!item
        });

        dispatch(listRequestSuccess(id, filteredData));
        dispatch(setTotalCount(id, size));
      })
      .catch((err) => {
        requestController = null;
        dispatch(listRequestError(id, err));
      });
  } catch {
    requestController = null;
    dispatch(listRequestError(id, 'Something went wrong'));
  }
};

export const getOldSSPData =
  (id, url, pageToken = 0, pageSize = 10) =>
  (dispatch) => {
    dispatch(listRequest(id));
    pageSize = 999;
    APIClient.get(url, { params: { page_token: pageToken, page_size: pageSize } })
      .then((resp) => {
        const data = result(resp, 'data.payloads', null);
        const size = result(resp, 'data.total_size', 0);
        const filteredData = data.filter((item) => !!item);

        dispatch(listRequestSuccess(id, filteredData));
        dispatch(setTotalCount(id, size));
      })
      .catch((err) => {
        dispatch(listRequestError(id, err));
      });
  };

export const setActiveTab = (tab, navigate, ownProps) => (dispatch) => {
  const currentPath = window.location.pathname;

  if (ownProps.modal) {
    if (currentPath.startsWith('/dashboard')) {
      const uuid = get(ownProps, 'chargerId');
      const path = `/dashboard/details/${uuid}/${tab}`;

      navigate(path, { replace: true });
    }
    if (currentPath.startsWith('/chargers/multiple_fw_update')) {
      const uuid = get(ownProps, 'chargerId');
      const path = `/chargers/multiple_fw_update/details/${uuid}/${tab}`;

      navigate(path, { replace: true });
    }
    if (currentPath.startsWith('/plaza_list/chargers')) {
      const uuid = get(ownProps, 'chargerId');
      const path = `/plaza_list/chargers/details/${uuid}/${tab}`;

      navigate(path, { replace: true });
    }
  } else {
    const route0param = get(ownProps, 'match.params.actionType');
    const uuid = get(ownProps, 'match.params.chargerId');
    const path = `/chargers/${route0param}/${uuid}/${tab}`;

    navigate(path, { replace: true });
  }
};

export const onChargerDetailsTabExport = (exportProps) => (dispatch) => {
  const { ssplistId, selectAll, items, filter, sorting, c_uuid: chargerUuid } = exportProps;

  let endpoint = '';
  let filename = '';

  switch (ssplistId) {
    case faultsSSPId: // Export Faults
      endpoint = `/chargers/${chargerUuid}/failures/export`;
      filename = 'exported-faults';
      break;
    case sessionsTabTableId: // Export Sessions
      endpoint = `/chargers/${chargerUuid}/sessions/export`;
      filename = 'exported-sessions';
      break;
    case activitiesTabTableId: // Export Activities
      endpoint = `/chargers/${chargerUuid}/activities/export`;
      filename = 'exported-activities';
      break;
    default:
      throw new Error(`Unknown ssp-table id ${ssplistId}`);
  }

  const payload = getExportPayload(filter, selectAll, items);

  return exportFile(endpoint, payload, sorting, filename, dispatch);
};

const selectFormValue = (requested, current, status) => (status === parameterStatuses.LIVE ? current : requested);

export const processConfiguration = (configuration, dispatch) => {
  const formValues = {};
  const formPathsMap = {};
  const nonLiveParameters = [];

  configuration.nodes.forEach((node, i) => {
    node.groups.forEach((group, j) => {
      group.parameters.forEach((parameter, k) => {
        const {
          parameter_name: parameterName,
          value,
          requested_value: requestedValue,
          status,
          controller_type: controllerType,
          available_values: availableValues,
        } = parameter;

        const prefix = processPackagesFieldName(node.node_name, parameterName);

        formValues[prefix] = selectFormValue(requestedValue, value, parameter.status);
        formPathsMap[prefix] = {
          pathToParameter: `nodes.${i}.groups.${j}.parameters.${k}`,
          node: node.node_name,
          defaultName: parameterName,
          requested_value: requestedValue,
          value,
          status,
          controller_type: controllerType,
          available_values: availableValues,
        };
        if (![parameterStatuses.LIVE, parameterStatuses.REJECTED].includes(parameter.status)) {
          nonLiveParameters.push(prefix);
        }
      });
    });
  });

  dispatch(initialize(chargerConfigurationFormId, formValues));
  dispatch(setChargerConfiguration(configuration));
  dispatch(setConfigurationFieldPathMap(formPathsMap));
  dispatch(selectChargerConfigurationModule(get(configuration.nodes, '0.node_name', null)));
  dispatch(setNonLiveParametersExist(!!size(nonLiveParameters)));
};

export const loadChargerConfiguration = (uuid) => (dispatch) => {
  dispatch(reset(chargerConfigurationFormId));
  APIClient.get(`/chargers/${uuid}/config_parameters`)
    .then((response) => {
      const configuration = get(response, 'data', {});

      configuration.nodes = sortBy(configuration.nodes, (node) => node.node_name.toLowerCase());
      processConfiguration(configuration, dispatch);
    })
    .catch((err) => {
      let configError;

      if ([500, 503].includes(err.request.status)) {
        configError = 'Something went wrong. Please try to select fewer number of chargers.';
      }

      if (err.request.status === 403) {
        configError = 'There are no visible Configuration Parameters for this Access Level.';
      }

      dispatch(
        setChargerConfiguration({
          error: configError,
        }),
      );
    });
};

export const updateChargerConfiguration = (uuid, updatedParameters, lockInterval) => (dispatch) => {
  dispatch(setConfigurationParametersSubmitError(null));
  dispatch(setConfigurationSubmitFetching(true));

  APIClient.post(`/chargers/${uuid}/config_parameters`, updatedParameters)
    .then((response) => {
      const configuration = get(response, 'data', {});

      processConfiguration(configuration, dispatch);
      dispatch(toggleConfigurationConfirmationDialogOpen(false));
      dispatch(setConfigurationSubmitFetching(false));
      clearInterval(lockInterval);
    })
    .catch((err) => {
      dispatch(setConfigurationSubmitFetching(false));
      dispatch(setConfigurationParametersSubmitError(get(err, 'response.data.message', err.message)));
    });
};

export const getLockParams = (ids) => ({
  lock_type: 'CHARGER_CONFIGURATION',
  object_uuids: Array.isArray(ids) ? ids : [ids],
});

export const setLockConfigParamsInterval = (lockParameters) => (dispatch) => {
  const lockInterval = setInterval(
    () => {
      APIClient.put(`/functionality-lock`, lockParameters).catch(() => {});
    },
    14 * 60 * 1000,
  );
  dispatch(setChargerConfigurationLockInterval(lockInterval));
};

export const setConfigurationLockByAnotherUser = (isLocked) => ({
  type: 'SET_LOCK_CONFIGURATION_BY_ANOTHER_USER',
  isLocked,
});

export const unlockChargerConfiguration = (chargerUUID) => (dispatch) => {
  if (!chargerUUID) return;

  const lockParameters = getLockParams(chargerUUID);

  APIClient.delete('/functionality-lock', { data: lockParameters })
    .then(() => {
      dispatch(setConfigurationLockByAnotherUser(false));
      dispatch(toggleShowConfigurationLockedPopup(false));
      dispatch(setChargerConfigurationLockedIds(null));
    })
    .catch(({ response: { status } }) => {
      dispatch(toggleShowConfigurationLockedPopup(true));

      if (status === 400) {
        dispatch(setConfigurationLockByAnotherUser(true));
      }
    });
};

export const unlockLockedConfiguration = () => (dispatch, getState) => {
  const state = getState();
  const lockedIds = selectLockedConfigurationIds(state);
  if (lockedIds) {
    dispatch(unlockChargerConfiguration(lockedIds));
  }
}

export const lockChargerConfiguration = (uuid) => (dispatch) => {
  const lockParameters = getLockParams(uuid);

  APIClient.post('/functionality-lock', lockParameters)
    .then(() => {
      dispatch(setLockConfigParamsInterval(lockParameters));
      dispatch(setChargerConfigurationLockedIds(uuid));
    })
    .catch((err) => {
      const isLocked = get(err, 'response.data.errors', []).find((item) => item.code === 105);

      dispatch(toggleShowConfigurationLockedPopup(isLocked));
    });
};

export const resetFields = (fields) => (dispatch, getState) => {
  const state = getState();
  const chargerUuid = getViewedChargerUuid(state);
  const fieldsPathMap = cloneDeep(getViewedChargerFieldsPathMap(state));
  const configurationForm = getChargerConfigurationFormData(state);
  const configuration = getViewedChargerConfiguration(state);

  const updatedFieldSet = cloneDeep(configurationForm.initialValues);

  const updatedConfiguration = fields.reduce((config, field) => {
    const { nodeName, parameterName } = field;
    const prefix = processPackagesFieldName(nodeName, parameterName);

    const resettingField = fieldsPathMap[prefix];

    updatedFieldSet[prefix] = resettingField.value;
    resettingField.status = parameterStatuses.LIVE;
    fieldsPathMap[prefix] = resettingField;

    const resatFieldInConfiguration = get(config, resettingField.pathToParameter);

    resatFieldInConfiguration.status = parameterStatuses.LIVE;
    set(config, resettingField.pathToParameter, resatFieldInConfiguration);

    return config;
  }, configuration);

  APIClient.post(`/chargers/${chargerUuid}/reset-config-parameters-batch`, fields).then(() => {
    dispatch(initialize(chargerConfigurationFormId, updatedFieldSet, true));
    dispatch(setChargerConfiguration(updatedConfiguration));
    dispatch(setConfigurationFieldPathMap(fieldsPathMap));
    dispatch(toggleReinitForm(true));
  });
};

export const resetRejectedFields = () => (dispatch, getState) => {
  const state = getState();
  const { modifiedValues } = getChargerConfigurationFormData(state);
  const rejectedParams = getViewedChargerConfigurationParametersByStatus(state, parameterStatuses.REJECTED);

  const rejectedFields = rejectedParams.reduce((acc, param) => {
    const { node_name: nodeName, parameter_name: parameterName } = param;
    const prefix = processPackagesFieldName(nodeName, parameterName);

    if (prefix in modifiedValues) {
      acc.push({
        nodeName,
        parameterName,
      });
    }

    return acc;
  }, []);

  if (rejectedFields.length) {
    dispatch(resetFields(rejectedFields));
  }
};

export const checkInteractionDiscovery = (chargerId) => (dispatch) =>
  ChargersTask.interactions({ chargerId }).then(({ data }) => {
    const value = Boolean(data.find((item) => item.type === DIAGNOSTICS_CHARGER_TASK_TYPE));

    dispatch(setDiagnosticsAvailable(value));
  });
